/**
 * Created by shmbgd on 29.11.22.
 */
import * as React from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { LoadingContainer } from './Components/LoadingContainer';
import { useEffect, useState } from 'react';
import { useRootStore } from './Store/useRootStore';
import { observer } from 'mobx-react';
import { MainLayout } from './View/Layout/MainLayout';
import { AcademyScreen } from './View/Academy/AcademyScreen';
import { NutritionIndexScreen } from './View/Nutrition/NutritionIndexScreen';
import { NutritionLibraryScreen } from './View/Nutrition/Library/NutritionLibraryScreen';
import { MoveIndexScreen } from './View/Move/MoveIndexScreen';
import { AcademyContentViewScreen } from './View/Academy/View/AcademyContentViewScreen';
import { AccountScreen } from './View/Account/AccountScreen';
import { FeedbackScreen } from './View/Support/Feedback/FeedbackScreen';
import { HomeScreen } from './View/Home/HomeScreen';
import { ContactScreen } from './View/Support/Contact/ContactScreen';
import { PrivacyScreen } from './View/Support/Privacy/PrivacyScreen';
import { TermsScreen } from './View/Support/Terms/TermsScreen';
import { AcademyScheduleScreen } from './View/Academy/Schedule/AcademyScheduleScreen';
import { MoveContentViewScreen } from './View/Move/View/MoveContentViewScreen';
import { AcademyLibraryScreen } from './View/Academy/Library/AcademyLibraryScreen';
import { MoveOndemandLibraryScreen } from './View/Move/Library/MoveOndemandLibraryScreen';
import { MoveScheduleScreen } from './View/Move/Schedule/MoveScheduleScreen';
import { FitnessProgramsScreen, FitnessProgramDetailScreen } from './View/Move/FitnessPrograms';
import { BlogIndexScreen } from './View/Blog/BlogIndexScreen';
import { LoginScreen } from './View/Auth/Login/LoginScreen';
import { CatchAllRoute } from './View/CatchAllRoute';
import { ScheduleCalendarScreen } from './View/ScheduleCalendar/ScheduleCalendarScreen';
import { NutritionContentViewScreen } from './View/Nutrition/NutritionContentViewScreen';
import { ChallengeDetailScreen } from './View/Motivation/ChallengeDetailScreen';
import { MotivationScreen } from './View/Motivation/MotivationScreen';
import { ChallengeRankingScreen } from './View/Motivation/ChallengeRankingScreen';
import { TeamInvitationScreen } from './View/Motivation/TeamInvitationScreen';
import { ChallengeLocationScreen } from './View/Motivation/ChallengeLocationScreen';
import { MotivationHistoryScreen } from './View/Motivation/MotivationHistoryScreen';
import { MotivationCommunityScreen } from './View/Motivation/MotivationCommunityScreen';
import { MotivationChallengesScreen } from './View/Motivation/MotivationChallengesScreen';
import { ActivityLogScreen } from './View/Activity/ActivityLogScreen';
import { ActivityLogDetailScreen } from './View/Activity/Detail/ActivityLogDetailScreen';
import { TerraCallbackScreen } from './View/Account/TerraCallbackScreen';
import { CompanyScreen } from './View/Company/CompanyScreen';
import { CompanyContentViewScreen } from './View/Company/CompanyContentViewScreen';
import { ProgramDetailScreen } from './View/Account/ProgramDetailScreen';
import { useAuth0 } from '@auth0/auth0-react';
import { SmartAppBanner } from './Components/SmartAppBanner';
import { MentalHealthIndexScreen } from './View/MentalHealth/MentalHealthIndexScreen';
import { MentalHealthContentViewScreen } from './View/MentalHealth/MentalHealthContentViewScreen';
import { MentalHealthLibraryScreen } from './View/MentalHealth/MentalHealthLibraryScreen';
import { MindfulnessProgramsScreen } from './View/MentalHealth/MindfulnessPrograms/MindfulnessProgramsScreen';
import { MindfulnessProgramDetailScreen } from './View/MentalHealth/MindfulnessPrograms/MindfulnessProgramDetailScreen';

export type RootProps = {};

export const Root: React.FC<RootProps> = observer(() => {
  const [loading, setLoading] = useState(true);
  const { authentication, coachSubscription } = useRootStore();
  const location = useLocation();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const checkRedirect = React.useCallback(async () => {
    const isAuthPage = location.pathname.startsWith('/auth') || location.pathname.startsWith('/registration');

    const redirectToEducate = () => {
      const urlParams = new URLSearchParams(window.location.search);

      const linkId = urlParams.get('linkId');
      if (linkId) {
        const lang = urlParams.get('lang');
        const langQuery = lang ? `?lang=${encodeURIComponent(lang)}` : '';
        window.location.replace(`https://platform.kinastic.com/${linkId}${langQuery}`);
        return;
      }

      const gymId = urlParams.get('gymId');
      const hash = urlParams.get('hash');
      const slug = location.pathname.replace(/\/$/, '').split('/').filter(p => p).pop();
      if (gymId && hash) {
        window.location.replace(`https://platform.kinastic.com/${slug}?gymId=${gymId}&hash=${hash}`);
        return;
      }

      throw new Error('No linkId or gymId and hash found');
    };

    return getAccessTokenSilently({
      detailedResponse: true,
      timeoutInSeconds: 3,
      authorizationParams: {
        audience: 'https://api.kinastic.com',
        scope: 'offline_access openid profile email',
        redirect_uri: window.location.origin,
      },
    })
      .then((token) => authentication.storeAccessToken(token.access_token))
      .then(() => coachSubscription.fetchSubscription())
      .then(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const returnTo = urlParams.get('returnTo');
        return returnTo && returnTo.startsWith('/') ? returnTo : '/home';
      })
      .then((redirectPath) => {
        if (isAuthPage || location.pathname === '/') {
          navigate(redirectPath, { replace: true });
        }
      })
      .catch((error) => {
        console.error('Not authenticated, redirect to educate: ', error);
        redirectToEducate();
      })
      .catch((error) => {
        console.error('Error getting access token:', error);
        const returnTo = location.pathname !== '/' ? location.pathname + location.search : '';
        const authUrl = returnTo ? `/auth?returnTo=${encodeURIComponent(returnTo)}` : '/auth';
        return !isAuthPage && navigate(authUrl, { replace: true });
      });
  }, [getAccessTokenSilently, location.pathname, coachSubscription, authentication, navigate]);

  useEffect(() => {
    checkRedirect().finally(() => setLoading(false));
  }, [checkRedirect]);

  useEffect(() => {
    const canControlScrollRestoration = 'scrollRestoration' in window.history;
    if (canControlScrollRestoration) {
      window.history.scrollRestoration = 'manual';
    }

    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (loading) {
    return <LoadingContainer text="Loading Page" />;
  }

  return (
    <>
      <SmartAppBanner />
      <Routes>
      <Route path="/explore-tab/nutrition" element={<Navigate to="/nutrition" replace />} />
      <Route path="/explore-tab/fitness-programs" element={<Navigate to="/move/fitness-programs" replace />} />
      <Route path="/mindfulness-programs" element={<Navigate to="/academy" replace />} />
      <Route path="/explore-tab/mindfulness-programs" element={<Navigate to="/academy" replace />} />
      {/* Legacy redirect for old mindfulness programs URL */}
      <Route path="/*">
        <Route path="auth">
          <Route index element={<LoginScreen />} />
        </Route>
        <Route path="registration" >
          <Route index element={<LoginScreen />} />
        </Route>
        <Route path="tracker/terra/callback" element={<TerraCallbackScreen />} />
        <Route element={<MainLayout />}>
          {/* Default home page */}
          <Route index element={<HomeScreen />} />
          {coachSubscription.canShowAcademy && (
            <React.Fragment>
              <Route path="academy">
                <Route index element={<AcademyScreen />} />
                <Route path="schedule" element={<AcademyScheduleScreen />} />
                <Route path="library" element={<AcademyLibraryScreen />} />
                <Route path=":contentId" element={<AcademyContentViewScreen />} />
              </Route>
              <Route path="mental-health">
                <Route index element={<MentalHealthIndexScreen />} />
                <Route path="library" element={<MentalHealthLibraryScreen />} />
                <Route path="mindfulness-programs" element={<MindfulnessProgramsScreen />} />
                <Route path="mindfulness-programs/:templateId" element={<MindfulnessProgramDetailScreen />} />
                <Route path=":contentId" element={<MentalHealthContentViewScreen />} />
              </Route>
              <Route path="nutrition">
                <Route index element={<NutritionIndexScreen />} />
                <Route path="library" element={<NutritionLibraryScreen />} />
                <Route path=":contentId" element={<NutritionContentViewScreen />} />
              </Route>
              <Route path="blog" element={<BlogIndexScreen />} />
            </React.Fragment>
          )}
        {/* Home page */}
        <Route path="home" element={<HomeScreen />} />
        {coachSubscription.canShowMove && (
            <Route path="move">
              <Route index element={<MoveIndexScreen />} />
              <Route path="schedule" element={<MoveScheduleScreen />} />
              <Route path="library" element={<MoveOndemandLibraryScreen />} />
              <Route path="fitness-programs" element={<FitnessProgramsScreen />} />
              <Route path="fitness-programs/:templateId" element={<FitnessProgramDetailScreen />} />
              <Route path=":contentId" element={<MoveContentViewScreen />} />
            </Route>
          )}
          {/* Company-exclusive content */}
          <Route path="company">
            <Route index element={<CompanyScreen />} />
            <Route path=":contentId" element={<CompanyContentViewScreen />} />
          </Route>

          {/* Calendar overview for scheduled and done activities */}
          <Route path="schedule" element={<ScheduleCalendarScreen />} />

          {/* Motivation / Challenge pages */}
          <Route path="motivation">
            <Route index element={<MotivationScreen />} />
            <Route path="history" element={<MotivationHistoryScreen />} />
            <Route path="community" element={<MotivationCommunityScreen />} />
            <Route path="challenges" element={<MotivationChallengesScreen />} />
            <Route path="challenge/:challengeId" element={<ChallengeDetailScreen />} />
            <Route path="challenge/:challengeId/ranking" element={<ChallengeRankingScreen />} />
            <Route path="challenge/:challengeId/teams/:teamId/join" element={<TeamInvitationScreen />} />
            <Route path="challenge/:challengeId/location/:locationId" element={<ChallengeLocationScreen />} />
          </Route>

          {/* Activities page */}
          <Route path="activities">
            <Route index element={<ActivityLogScreen />} />
            <Route path=":activityLogId" element={<ActivityLogDetailScreen />} />
          </Route>

          <Route path="account">
            <Route index element={<AccountScreen />} />
            <Route path="program/:programType/:programId" element={<ProgramDetailScreen />} />
          </Route>
          <Route path="feedback">
            <Route index element={<FeedbackScreen />} />
          </Route>
          <Route path="contact" element={<ContactScreen />} />
          <Route path="privacy-policy" element={<PrivacyScreen />} />
          <Route path="terms" element={<TermsScreen />} />
          <Route path="*" element={<CatchAllRoute />} />
        </Route>
      </Route>
    </Routes>
    </>
  );
});
